import { FormEventHandler, memo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "stores";
import styled from "styled-components";
import store from "stores/interfaces";
import {
  AppBar,
  Toolbar,
  DialogContent,
  Switch,
  Button,
  TextField,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { ItemRecord } from "stores/modules/entities.room.items";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormClickAction from "./FormClickAction";
import { addUndo } from "stores/modules/entities.room.histories/slice";

export type FormData = {
  width: number;
  height: number;
  z: number;
  memo: string;
  locked: boolean;
  freezed: boolean;
  plane: boolean;

  clickAction: "none" | "link" | "message";
  linkUrl: string;
  messageText: string;
};

type OriginProps = {
  roomId: string;
  panelId: string;
  onClose: () => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
};

const PanelDetail = ({ roomId, panelId, onClose, onSubmit }: OriginProps) => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation();

  const panel =
    useAppSelector((state) => store.getRoomItemById(state, panelId)) ||
    blankItem;

  const { control, register } = useFormContext<FormData>();

  const onImageSelect = useCallback(() => {
    dispatch(
      store.appStateMutate((state) => {
        state.openRoomImageSelect = true;
        state.openRoomImageSelectDir = "item";
        state.openRoomImageSelectTarget = "item/update";
      })
    );
  }, [dispatch]);
  const onCoverImageSelect = useCallback(() => {
    dispatch(
      store.appStateMutate((state) => {
        state.openRoomImageSelect = true;
        state.openRoomImageSelectDir = "cover";
        state.openRoomImageSelectTarget = "item/updateCover";
      })
    );
  }, [dispatch]);
  const onDelete = useCallback(() => {
    if (window.confirm(t("本当に削除しますか？"))) {
      dispatch(store.deleteRoomItem(roomId, panelId));
      dispatch(
        addUndo({
          kind: "update-item",
          id: panelId,
          before: panel,
          after: null,
        })
      );
      dispatch(
        store.appStateMutate((state) => {
          state.openRoomPanelDetail = false;
        })
      );
    }
  }, [roomId, panelId, dispatch, t, panel]);

  if (!panel) return null;
  return (
    <>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar variant="dense">
          <Typography variant="subtitle2">
            {t("スクリーンパネル設定")}
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <Group>
            <TextField
              variant="standard"
              label={t("横幅")}
              margin="dense"
              type="number"
              {...register("width", { valueAsNumber: true })}
            />
            <TextField
              variant="standard"
              label={t("縦幅")}
              margin="dense"
              type="number"
              {...register("height", { valueAsNumber: true })}
            />
            <TextField
              variant="standard"
              label={t("重なり優先度")}
              margin="dense"
              type="number"
              {...register("z", { valueAsNumber: true })}
            />
          </Group>
          <TextField
            label={t("メモ")}
            variant="filled"
            multiline
            rows={4}
            maxRows={8}
            fullWidth
            {...register("memo")}
          />
          <List>
            <ListItem dense>
              <ListItemText
                primary={t("配置固定")}
                secondary={t("現在の位置とサイズでオブジェクトを固定")}
              />
              <ListItemSecondaryAction>
                <Controller
                  name="locked"
                  control={control}
                  render={({ field: { onChange, value, name, ref } }) => (
                    <Switch
                      onChange={onChange}
                      checked={value}
                      name={name}
                      inputRef={ref}
                    />
                  )}
                />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem dense>
              <ListItemText
                primary={t("サイズ固定")}
                secondary={t("現在のサイズのみを固定します")}
              />
              <ListItemSecondaryAction>
                <Controller
                  name="freezed"
                  control={control}
                  render={({ field: { onChange, value, name, ref } }) => (
                    <Switch
                      onChange={onChange}
                      checked={value}
                      name={name}
                      inputRef={ref}
                    />
                  )}
                />
              </ListItemSecondaryAction>
            </ListItem>
            <ListItem dense>
              <ListItemText
                primary={t("地形パネルとして扱う")}
                secondary={t("ショートカットキーによる操作を無効化します")}
              />
              <ListItemSecondaryAction>
                <Controller
                  name="plane"
                  control={control}
                  render={({ field: { onChange, value, name, ref } }) => (
                    <Switch
                      onChange={onChange}
                      checked={value}
                      name={name}
                      inputRef={ref}
                    />
                  )}
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
          <ButtonGroup>
            <Button onClick={onImageSelect} variant="outlined" fullWidth>
              {t("画像選択")}
            </Button>
            <Button onClick={onCoverImageSelect} variant="outlined" fullWidth>
              {t("背面画像選択")}
            </Button>
          </ButtonGroup>
          <StyledAccordion
            elevation={0}
            defaultExpanded={panel.clickAction != null}
          >
            <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t("高度な設定")}</Typography>
              <Typography variant="body2" color="textSecondary">
                {t("クリックアクションの設定ができます")}
              </Typography>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <FormClickAction />
            </StyledAccordionDetails>
          </StyledAccordion>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onDelete} fullWidth color="secondary">
          {t("削除")}
        </Button>
        <Button onClick={onClose} fullWidth color="primary">
          {t("保存")}
        </Button>
      </DialogActions>
    </>
  );
};

const Group = styled.div`
  display: flex;
  .MuiFormControl-root + .MuiFormControl-root {
    margin-left: 16px;
  }
`;

const ButtonGroup = styled.div`
  .MuiButton-root + .MuiButton-root {
    margin-top: 8px;
  }
`;

const StyledAccordion = styled(Accordion)`
  margin-top: 16px;

  background-color: unset;
  &.Mui-expanded {
    margin: auto;
    margin-top: 16px;
  }

  &:before {
    display: none;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  padding: 0;
  min-height: 48px;
  &.Mui-expanded {
    min-height: 48px;
  }
  .MuiAccordionSummary-content {
    flex-direction: column;
  }
  .MuiAccordionSummary-content.Mui-expanded {
    margin: 12px 0;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  flex-direction: column;
  padding-top: 24px;
`;

const blankItem = ItemRecord({});

export default memo(PanelDetail);
